/**
 * Bilingual content for all 6 dedicated service pages.
 * Keep this file the single source of truth; the ServicePage template
 * (../pages/ServicePage.jsx) renders any of these via slug lookup.
 */

const BASE_FAQ = {
  de: {
    timeline_q: "Wie lange dauert ein Projekt?",
    timeline_a: "Je nach Umfang zwischen 2 und 12 Wochen. Du erhältst nach unserem ersten Strategiegespräch eine konkrete Roadmap mit Meilensteinen — keine Black-Box, keine Überraschungen.",
    cost_q: "Was kostet ein Projekt?",
    cost_a: "Unsere Projekte starten ab €2.500. Konkrete Investitionen klären wir transparent im kostenfreien Strategiegespräch — basierend auf Umfang, Zeitrahmen und gewünschtem Ergebnis.",
    after_q: "Was passiert nach dem Launch?",
    after_a: "Wir bieten flexible Care-Pläne (Monatlich, Quartal oder On-Demand): Updates, Monitoring, Performance-Optimierung und Weiterentwicklung. Du wirst nicht im Stich gelassen.",
  },
  en: {
    timeline_q: "How long does a project take?",
    timeline_a: "Depending on scope, between 2 and 12 weeks. After our first strategy call you receive a concrete roadmap with milestones — no black box, no surprises.",
    cost_q: "What does a project cost?",
    cost_a: "Projects start at €2,500. We discuss concrete investment transparently in the free strategy call, based on scope, timeline and desired outcome.",
    after_q: "What happens after launch?",
    after_a: "We offer flexible care plans (monthly, quarterly or on-demand): updates, monitoring, performance optimization and continuous improvement. You are never left alone.",
  },
};

export const SERVICE_CONTENT = {
  "ai-automation": {
    icon: "Cpu",
    seoTitle: {
      de: "KI-Automatisierung & AI-Workflows | Joshi FusionTech Media",
      en: "AI Automation & Workflow Engineering | Joshi FusionTech Media",
    },
    seoDesc: {
      de: "Wir bauen KI-Agenten, intelligente Workflows und CRM-Automatisierung, die echte Stunden zurückgeben — von Lead-Capture bis zur internen Prozess-Optimierung. Made in Berlin.",
      en: "We build AI agents, intelligent workflows and CRM automation that give real hours back — from lead-capture to internal process optimization. Made in Berlin.",
    },
    hero: {
      de: {
        kicker: "KI & Automatisierung",
        h1: "KI-Automatisierung, die in Stunden — nicht Monaten — Wirkung zeigt.",
        sub: "Wir setzen GPT-, Claude- und Gemini-basierte Agenten zusammen mit n8n/Zapier-Workflows ein, um repetitive Arbeit zu eliminieren und gleichzeitig CRM-, E-Mail- und interne Systeme nahtlos zu verbinden — pragmatisch, datenschutzkonform und messbar.",
      },
      en: {
        kicker: "AI & Automation",
        h1: "AI automation that delivers in hours — not months.",
        sub: "We combine GPT-, Claude- and Gemini-based agents with n8n/Zapier workflows to eliminate repetitive work and connect CRM, email and internal systems — pragmatic, GDPR-compliant and measurable.",
      },
    },
    deliver: {
      de: {
        title: "Was wir liefern",
        items: [
          { t: "AI-Lead-Capture & Qualifizierung", d: "Conversational AI, die Leads qualifiziert, Termine bucht und an deinen Sales-Funnel übergibt." },
          { t: "CRM-Automatisierung", d: "Saubere Pipelines zwischen HubSpot, Pipedrive, Notion, Airtable, Google Sheets — ohne manuelle Datenpflege." },
          { t: "Interne AI-Copiloten", d: "Custom GPT-Assistenten für deinen Sales-, Support- oder Operations-Stack — mit RAG auf deine eigenen Daten." },
          { t: "Prozess-Automatisierung", d: "Workflows, die Bestellungen, Reportings, Mahnungen und Backoffice-Tasks selbstständig abwickeln." },
          { t: "Chatbot-Integration", d: "Mehrsprachige Chatbots auf Website, WhatsApp und Telegram, angebunden an deine Wissensdatenbank." },
          { t: "AI-Analytics", d: "Wir machen unstrukturierte Daten (E-Mails, Calls, PDFs) für Reporting & Forecast nutzbar." },
        ],
      },
      en: {
        title: "What we deliver",
        items: [
          { t: "AI lead-capture & qualification", d: "Conversational AI that qualifies leads, books meetings and hands off to your sales funnel." },
          { t: "CRM automation", d: "Clean pipelines between HubSpot, Pipedrive, Notion, Airtable, Google Sheets — without manual data entry." },
          { t: "Internal AI copilots", d: "Custom GPT assistants for your sales, support or ops stack — with RAG on your own data." },
          { t: "Process automation", d: "Workflows that handle orders, reporting, dunning and back-office tasks autonomously." },
          { t: "Chatbot integration", d: "Multilingual chatbots on website, WhatsApp and Telegram, connected to your knowledge base." },
          { t: "AI analytics", d: "We make unstructured data (emails, calls, PDFs) usable for reporting & forecasting." },
        ],
      },
    },
    benefits: {
      de: { title: "Nutzen für dein Business", items: ["Rund-um-die-Uhr Lead-Bearbeitung", "30–70 % weniger manuelle Routine-Tasks", "Schnellere Reaktionszeiten gegenüber Wettbewerbern", "Skalierbar ohne lineares Hiring", "DSGVO-konform, EU-Hosting wo möglich"] },
      en: { title: "Business benefits", items: ["24/7 lead handling", "30–70% fewer manual routine tasks", "Faster response times vs. competitors", "Scalable without linear hiring", "GDPR-compliant, EU hosting where possible"] },
    },
    stack: {
      de: { title: "Technologie-Stack", items: ["OpenAI (GPT-5.2)", "Anthropic (Claude Sonnet 4.5)", "Google Gemini 3 Pro", "n8n", "Zapier / Make", "LangChain", "Pinecone / Weaviate", "FastAPI / Python", "Supabase / PostgreSQL"] },
      en: { title: "Technology stack", items: ["OpenAI (GPT-5.2)", "Anthropic (Claude Sonnet 4.5)", "Google Gemini 3 Pro", "n8n", "Zapier / Make", "LangChain", "Pinecone / Weaviate", "FastAPI / Python", "Supabase / PostgreSQL"] },
    },
    process: {
      de: { title: "Unser Prozess", steps: [
        { t: "Discovery & Audit", d: "Wir analysieren bestehende Workflows, Datenquellen und identifizieren die größten Time-Sinks." },
        { t: "Architektur", d: "Wir entwerfen eine Automatisierungslandschaft inkl. Datenschutz-, Failover- und Logging-Konzept." },
        { t: "Pilot", d: "Wir implementieren einen ersten Workflow in 1–2 Wochen, mit klar messbarer Baseline." },
        { t: "Rollout", d: "Wir skalieren auf weitere Use-Cases, dokumentieren und schulen dein Team." },
        { t: "Optimierung", d: "Monatliches Reporting, kontinuierliche Verbesserung und neue Use-Cases." },
      ]},
      en: { title: "Our process", steps: [
        { t: "Discovery & audit", d: "We map existing workflows, data sources and identify the biggest time-sinks." },
        { t: "Architecture", d: "We design an automation landscape including privacy, failover and logging concept." },
        { t: "Pilot", d: "We ship a first workflow in 1–2 weeks with a clearly measurable baseline." },
        { t: "Rollout", d: "We scale to more use-cases, document, and train your team." },
        { t: "Optimization", d: "Monthly reporting, continuous improvement and new use-cases." },
      ]},
    },
    cases: {
      de: { title: "Showcase-Beispiele", items: [
        { industry: "E-Commerce (DACH)", challenge: "Manueller Support für 200+ Anfragen pro Tag.", solution: "GPT-gestützter First-Line-Support mit RAG auf Produktdaten und Retouren-Workflow.", result: "Support-Team fokussiert auf VIP-Tickets; Antwortzeit unter 60 Sekunden." },
        { industry: "B2B SaaS Startup", challenge: "Inbound-Leads verbrannten weil Qualifizierung Tage dauerte.", solution: "AI-Qualifizierungs-Agent + Calendly-Routing direkt nach Form-Submit.", result: "Vom Lead zum gebuchten Demo in Minuten statt Tagen." },
      ]},
      en: { title: "Showcase examples", items: [
        { industry: "E-commerce (DACH)", challenge: "Manual support for 200+ daily tickets.", solution: "GPT-powered first-line support with RAG on product data and returns workflow.", result: "Support team focused on VIP tickets; response under 60 seconds." },
        { industry: "B2B SaaS startup", challenge: "Inbound leads burned because qualification took days.", solution: "AI qualification agent + Calendly routing right after form submit.", result: "From lead to booked demo in minutes instead of days." },
      ]},
    },
    faq: {
      de: [
        { q: "Ist KI-Automatisierung DSGVO-konform möglich?", a: "Ja. Wir wählen LLM-Anbieter mit EU-Hosting oder strikten Datenverarbeitungsvereinbarungen, anonymisieren sensible Inputs und dokumentieren jeden Datenfluss." },
        { q: "Brauchen wir interne Entwickler?", a: "Nein. Wir liefern schlüsselfertig und schulen dein Team. Optional kannst du nach dem Launch unsere Care-Pläne nutzen." },
        { q: "Welche LLMs setzt ihr ein?", a: "Wir wählen das Modell pro Use-Case: GPT-5.2 für komplexe Reasoning-Tasks, Claude für lange Kontexte, Gemini für multimodale Anwendungen, lokale Modelle wenn nötig." },
        { q: BASE_FAQ.de.timeline_q, a: BASE_FAQ.de.timeline_a },
        { q: BASE_FAQ.de.cost_q, a: BASE_FAQ.de.cost_a },
        { q: BASE_FAQ.de.after_q, a: BASE_FAQ.de.after_a },
      ],
      en: [
        { q: "Is AI automation possible in a GDPR-compliant way?", a: "Yes. We pick LLM providers with EU hosting or strict data-processing agreements, anonymize sensitive inputs, and document every data flow." },
        { q: "Do we need internal developers?", a: "No. We deliver turnkey and train your team. Optionally, our care plans take over post-launch." },
        { q: "Which LLMs do you use?", a: "We pick the model per use-case: GPT-5.2 for complex reasoning, Claude for long contexts, Gemini for multimodal, local models if needed." },
        { q: BASE_FAQ.en.timeline_q, a: BASE_FAQ.en.timeline_a },
        { q: BASE_FAQ.en.cost_q, a: BASE_FAQ.en.cost_a },
        { q: BASE_FAQ.en.after_q, a: BASE_FAQ.en.after_a },
      ],
    },
  },

  "web-development": {
    icon: "Code2",
    seoTitle: {
      de: "Webentwicklung & moderne Websites | Joshi FusionTech Media",
      en: "Web Development & Modern Websites | Joshi FusionTech Media",
    },
    seoDesc: {
      de: "Performante Business-Websites, Landingpages, Corporate-Sites und E-Commerce — React, Next.js, Webflow oder Headless. Conversion-optimiert, DSGVO-konform, mobil zuerst.",
      en: "High-performance business websites, landing pages, corporate sites and e-commerce — React, Next.js, Webflow or headless. Conversion-optimized, GDPR-compliant, mobile-first.",
    },
    hero: {
      de: { kicker: "Web Development", h1: "Websites, die laden, ranken und konvertieren.", sub: "Wir bauen moderne Websites, Landingpages und E-Commerce-Plattformen mit Performance, SEO und Conversion als Designprinzipien — von der ersten Skizze bis zum sechsten Lighthouse-Audit." },
      en: { kicker: "Web Development", h1: "Websites that load, rank and convert.", sub: "We build modern websites, landing pages and e-commerce platforms with performance, SEO and conversion as design principles — from the first sketch to the sixth Lighthouse audit." },
    },
    deliver: {
      de: { title: "Was wir liefern", items: [
        { t: "Business-Websites", d: "Mehrseitige Corporate-Auftritte mit CMS, Mehrsprachigkeit und perfekten Core Web Vitals." },
        { t: "Landingpages", d: "Conversion-getriebene Seiten für Kampagnen, Produkte oder Lead-Magneten." },
        { t: "Corporate Websites", d: "Für KMU und Konzerne — mit Editor-Workflows, Approval-Prozessen und IT-Compliance." },
        { t: "E-Commerce", d: "Shopify, WooCommerce, Medusa oder Headless mit Stripe/Adyen-Integration." },
        { t: "Website-Optimierung", d: "Performance-, SEO- und Conversion-Audits inkl. Implementierung der Empfehlungen." },
        { t: "Wartung & Hosting", d: "Updates, Monitoring, Backups, Security-Patches — wir kümmern uns." },
      ]},
      en: { title: "What we deliver", items: [
        { t: "Business websites", d: "Multi-page corporate sites with CMS, multilingual support and perfect Core Web Vitals." },
        { t: "Landing pages", d: "Conversion-driven pages for campaigns, products or lead magnets." },
        { t: "Corporate websites", d: "For SMBs and enterprises — with editor workflows, approval processes and IT compliance." },
        { t: "E-commerce", d: "Shopify, WooCommerce, Medusa or headless with Stripe/Adyen integration." },
        { t: "Website optimization", d: "Performance, SEO and conversion audits including implementation of the recommendations." },
        { t: "Maintenance & hosting", d: "Updates, monitoring, backups, security patches — we handle it." },
      ]},
    },
    benefits: {
      de: { title: "Warum unsere Websites anders sind", items: ["Mobile-first und barrierearm nach WCAG AA", "Core Web Vitals 90+ Lighthouse-Scores", "On-Page-SEO baseline standardmäßig", "Cookie-banner-frei wo möglich (DSGVO-Konformität)", "Versioniertes Code- und Content-Hosting"] },
      en: { title: "Why our websites are different", items: ["Mobile-first and accessible per WCAG AA", "Core Web Vitals 90+ Lighthouse scores", "On-page SEO baseline by default", "Cookie-banner-free where possible (GDPR-friendly)", "Versioned code and content hosting"] },
    },
    stack: {
      de: { title: "Technologie-Stack", items: ["React 19 / Next.js 14", "Astro", "Tailwind CSS", "Webflow / Headless CMS (Sanity, Contentful)", "Shopify / WooCommerce / Medusa", "Vercel / Cloudflare Pages", "Stripe / Adyen", "Plausible / GA4"] },
      en: { title: "Technology stack", items: ["React 19 / Next.js 14", "Astro", "Tailwind CSS", "Webflow / headless CMS (Sanity, Contentful)", "Shopify / WooCommerce / Medusa", "Vercel / Cloudflare Pages", "Stripe / Adyen", "Plausible / GA4"] },
    },
    process: {
      de: { title: "Entwicklungs-Prozess", steps: [
        { t: "Discovery", d: "Ziele, Zielgruppen, Wettbewerb und Conversion-Pfade definieren." },
        { t: "Wireframes", d: "Niedrige-Fidelity-Skizzen und Content-Architektur abstimmen." },
        { t: "Design", d: "High-Fidelity Design im Designsystem — Mobile & Desktop." },
        { t: "Build & Test", d: "Implementierung, Cross-Browser-Tests, Lighthouse, Accessibility." },
        { t: "Launch & Optimierung", d: "Go-Live, Monitoring und kontinuierliche A/B-Tests." },
      ]},
      en: { title: "Development process", steps: [
        { t: "Discovery", d: "Define goals, audiences, competition and conversion paths." },
        { t: "Wireframes", d: "Low-fidelity sketches and content architecture sign-off." },
        { t: "Design", d: "High-fidelity design in the design system — mobile & desktop." },
        { t: "Build & test", d: "Implementation, cross-browser tests, Lighthouse, accessibility." },
        { t: "Launch & optimization", d: "Go-live, monitoring and continuous A/B testing." },
      ]},
    },
    cases: {
      de: { title: "Portfolio-Beispiele", items: [
        { industry: "B2B SaaS", challenge: "Veraltetes WordPress, langsame Performance, schwache Conversion.", solution: "Neu in Next.js, modulares CMS, A/B-Testing-Setup.", result: "Schnellerer Seitenaufbau, klarer Funnel zu Demo-Buchungen." },
        { industry: "Lokale Dienstleister", challenge: "Keine Online-Präsenz, kein SEO.", solution: "Maßgeschneiderte Business-Website + Google-Business-Setup.", result: "Sichtbarkeit in lokalen Suchergebnissen, qualifizierte Anfragen via Web." },
      ]},
      en: { title: "Portfolio examples", items: [
        { industry: "B2B SaaS", challenge: "Outdated WordPress, slow performance, weak conversion.", solution: "Rebuilt in Next.js, modular CMS, A/B testing setup.", result: "Faster page builds, clear funnel to demo bookings." },
        { industry: "Local service providers", challenge: "No online presence, no SEO.", solution: "Custom business website + Google Business setup.", result: "Visibility in local search results, qualified web inquiries." },
      ]},
    },
    faq: {
      de: [
        { q: "Mit welchem Framework baut ihr?", a: "Wir wählen pro Projekt: Next.js/Astro für Performance-getriebene Marketing-Sites, Webflow für Marketing-Teams die selbst editieren wollen, Shopify/Medusa für E-Commerce. Wir bauen mit dem Tool das deinem Team danach das Leben leicht macht." },
        { q: "Ist meine Website mit eurem Build DSGVO-konform?", a: "Ja. Wir vermeiden Tracking-Tools die ein Cookie-Banner erfordern, hosten in der EU und implementieren konsentbasierte Analytics wenn nötig." },
        { q: "Bekomme ich den Quellcode?", a: "Ja, du erhältst vollen Zugriff auf das Git-Repository. Du bist Eigentümer:in des Codes — kein Vendor-Lock-in." },
        { q: BASE_FAQ.de.timeline_q, a: BASE_FAQ.de.timeline_a },
        { q: BASE_FAQ.de.cost_q, a: BASE_FAQ.de.cost_a },
        { q: BASE_FAQ.de.after_q, a: BASE_FAQ.de.after_a },
      ],
      en: [
        { q: "Which framework do you build with?", a: "We choose per project: Next.js/Astro for performance-driven marketing sites, Webflow for marketing teams who want to edit themselves, Shopify/Medusa for e-commerce. We build with the tool that makes your team's life easy afterwards." },
        { q: "Is my website GDPR-compliant with your build?", a: "Yes. We avoid tracking tools that require a cookie banner, host in the EU and implement consent-based analytics when needed." },
        { q: "Do I get the source code?", a: "Yes, you receive full access to the Git repository. You own the code — no vendor lock-in." },
        { q: BASE_FAQ.en.timeline_q, a: BASE_FAQ.en.timeline_a },
        { q: BASE_FAQ.en.cost_q, a: BASE_FAQ.en.cost_a },
        { q: BASE_FAQ.en.after_q, a: BASE_FAQ.en.after_a },
      ],
    },
  },

  "digital-marketing": {
    icon: "BarChart3",
    seoTitle: {
      de: "Digital Marketing, SEO & Google Ads | Joshi FusionTech Media",
      en: "Digital Marketing, SEO & Google Ads | Joshi FusionTech Media",
    },
    seoDesc: {
      de: "Daten-getriebenes Digital Marketing für KMU und Wachstumsfirmen: technisches SEO, Google Ads, Content, Conversion-Optimierung und Analytics — Berlin, Deutschland, EU.",
      en: "Data-driven digital marketing for SMBs and growth companies: technical SEO, Google Ads, content, conversion optimization and analytics — Berlin, Germany, EU.",
    },
    hero: {
      de: { kicker: "Digital Marketing", h1: "Sichtbarkeit, die zu Umsatz wird.", sub: "Wir verbinden technisches SEO, performante Ads und kontinuierliche Conversion-Optimierung zu einem Marketing-System — mit klaren Dashboards, ehrlichen Berichten und einem Fokus auf qualifizierten Leads statt Vanity-Metriken." },
      en: { kicker: "Digital Marketing", h1: "Visibility that turns into revenue.", sub: "We combine technical SEO, performance ads and continuous conversion optimization into one marketing system — with clear dashboards, honest reporting, and a focus on qualified leads, not vanity metrics." },
    },
    services: {
      de: { title: "Leistungen im Detail", items: [
        { t: "Technical SEO", d: "Crawl-Audits, Schema-Markup, Core Web Vitals, internationale SEO." },
        { t: "Google & Meta Ads", d: "Suchanzeigen, Performance Max, Display- und LinkedIn-/Meta-Kampagnen." },
        { t: "Analytics & Tracking", d: "GA4, Server-Side-Tracking, Consent-Mode v2, Looker-Studio-Dashboards." },
        { t: "Content Marketing", d: "Redaktionsplan, SEO-Texte, Pillar-Pages, Backlink-Strategie." },
        { t: "Conversion-Optimierung", d: "A/B-Tests, Heatmaps, Funnel-Analyse, Landingpage-Sprints." },
        { t: "Lead Generation", d: "Magneten, Webinare, E-Mail-Nurturing und CRM-Integration." },
      ]},
      en: { title: "Services in detail", items: [
        { t: "Technical SEO", d: "Crawl audits, schema markup, Core Web Vitals, international SEO." },
        { t: "Google & Meta Ads", d: "Search, Performance Max, Display, LinkedIn / Meta campaigns." },
        { t: "Analytics & tracking", d: "GA4, server-side tracking, Consent Mode v2, Looker Studio dashboards." },
        { t: "Content marketing", d: "Editorial plan, SEO copy, pillar pages, backlink strategy." },
        { t: "Conversion optimization", d: "A/B tests, heatmaps, funnel analysis, landing-page sprints." },
        { t: "Lead generation", d: "Magnets, webinars, email nurturing and CRM integration." },
      ]},
    },
    education: {
      de: {
        title: "Warum Digital Marketing wichtig ist",
        intro: "Digital Marketing ist kein optionaler Werbekanal mehr — es ist das Rückgrat moderner B2B- und B2C-Akquise. Hier ist, was es für dein Unternehmen leistet:",
        why: [
          { t: "Erhöht die Online-Sichtbarkeit", d: "Sichtbar sein, wo Käufer:innen suchen — Google, LinkedIn, YouTube." },
          { t: "Generiert qualifizierte Leads", d: "Targeting auf Personas und Kaufabsicht statt Massen-Reichweite." },
          { t: "Stärkt Brand Authority", d: "Konsistente Sichtbarkeit, Content-Tiefe und SEO bauen Vertrauen auf." },
          { t: "Verbessert Customer Acquisition", d: "Niedrigere CAC durch optimierte Funnels und kontinuierliches Testing." },
          { t: "Treibt langfristiges Wachstum", d: "Organische Kanäle wirken kumulativ — jeder Monat baut auf dem vorherigen auf." },
        ],
        analyticsTitle: "Wie Analytics & Tracking dein Business voranbringen",
        analytics: [
          "Misst tatsächliche Kampagnen-Performance statt Bauchgefühl",
          "Identifiziert Wachstumschancen und stagnierende Kanäle",
          "Verbessert den ROI durch klare Attribution",
          "Optimiert das Marketing-Budget zugunsten der besten Kanäle",
          "Ermöglicht datengetriebene Entscheidungen statt Politik",
        ],
        seoTitle: "Warum SEO wichtig bleibt",
        seo: [
          "Verbessert die Sichtbarkeit in Suchmaschinen langfristig",
          "Generiert nachhaltigen, kostenlosen Traffic",
          "Baut Vertrauen und Autorität in deiner Nische auf",
          "Reduziert die Abhängigkeit von bezahlten Anzeigen",
          "Funktioniert auch dann weiter, wenn Werbe-Budgets pausieren",
        ],
      },
      en: {
        title: "Why digital marketing matters",
        intro: "Digital marketing is no longer an optional ad channel — it is the backbone of modern B2B and B2C acquisition. Here is what it does for your business:",
        why: [
          { t: "Increases online visibility", d: "Be visible where buyers actually search — Google, LinkedIn, YouTube." },
          { t: "Generates qualified leads", d: "Targeting on personas and intent instead of mass reach." },
          { t: "Builds brand authority", d: "Consistent visibility, content depth and SEO build trust." },
          { t: "Improves customer acquisition", d: "Lower CAC through optimized funnels and continuous testing." },
          { t: "Drives long-term growth", d: "Organic channels compound — every month builds on the last." },
        ],
        analyticsTitle: "How analytics & tracking move your business forward",
        analytics: [
          "Measures actual campaign performance, not gut feeling",
          "Identifies growth opportunities and stagnating channels",
          "Improves ROI through clear attribution",
          "Optimizes marketing spend toward best-performing channels",
          "Enables data-driven decisions instead of politics",
        ],
        seoTitle: "Why SEO still matters",
        seo: [
          "Improves search-engine visibility for the long term",
          "Generates sustainable, free organic traffic",
          "Builds trust and authority in your niche",
          "Reduces dependency on paid ads",
          "Keeps working even when ad budgets are paused",
        ],
      },
    },
    process: {
      de: { title: "Strategie-Prozess", steps: [
        { t: "Audit", d: "Technisches SEO, Wettbewerb, Tracking und bisherige Kampagnen analysieren." },
        { t: "Strategie", d: "Persona-, Keyword- und Kanalstrategie definieren — mit klaren KPIs." },
        { t: "Setup", d: "Tracking, Tools, Kampagnenstruktur und Content-Pipeline aufsetzen." },
        { t: "Execution", d: "Kontinuierliches Publishing, Bidding, Testing — wöchentliche Stand-Ups." },
        { t: "Optimierung", d: "Monatliche Reviews, neue Tests, fortlaufendes Lernen." },
      ]},
      en: { title: "Strategy process", steps: [
        { t: "Audit", d: "Analyze technical SEO, competition, tracking and previous campaigns." },
        { t: "Strategy", d: "Define persona, keyword and channel strategy — with clear KPIs." },
        { t: "Setup", d: "Implement tracking, tools, campaign structure and content pipeline." },
        { t: "Execution", d: "Continuous publishing, bidding, testing — weekly stand-ups." },
        { t: "Optimization", d: "Monthly reviews, new tests, continuous learning." },
      ]},
    },
    cases: {
      de: { title: "Showcase-Beispiele", items: [
        { industry: "B2B Software", challenge: "Hohe Ads-Spend, niedrige Demo-Bookings.", solution: "Persona-Refactor, Landingpage-Sprint und Conversion-Tracking neu aufgesetzt.", result: "Klarerer Funnel, weniger Streuverlust, stabiler Lead-Flow." },
        { industry: "E-Commerce", challenge: "Stagnierender organischer Traffic.", solution: "Technisches SEO-Audit + Pillar-Page-Strategie + Backlink-Outreach.", result: "Schrittweiser organischer Aufwärtstrend und stabilere ROAS." },
      ]},
      en: { title: "Showcase examples", items: [
        { industry: "B2B Software", challenge: "High ad spend, low demo bookings.", solution: "Persona refactor, landing-page sprint and conversion tracking rebuilt.", result: "Clearer funnel, less wasted spend, steady lead flow." },
        { industry: "E-commerce", challenge: "Stagnating organic traffic.", solution: "Technical SEO audit + pillar-page strategy + backlink outreach.", result: "Gradual organic uptrend and more stable ROAS." },
      ]},
    },
    faq: {
      de: [
        { q: "Garantiert ihr bestimmte Rankings?", a: "Nein — niemand seriös tut das. Wir garantieren saubere Methodik, ehrliche Reports und nachhaltige Strategien statt kurzfristiger Tricks." },
        { q: "Macht ihr auch nur SEO oder nur Ads?", a: "Ja, beides ist möglich. Maximale Wirkung sehen wir aber, wenn SEO, Ads und Conversion-Optimierung ineinandergreifen." },
        { q: "Wie sieht das Reporting aus?", a: "Du bekommst ein Looker-Studio-Dashboard plus einen monatlichen Bericht mit Klartext: Was lief, was lernen wir, was probieren wir nächstes Mal." },
        { q: BASE_FAQ.de.timeline_q, a: BASE_FAQ.de.timeline_a },
        { q: BASE_FAQ.de.cost_q, a: BASE_FAQ.de.cost_a },
        { q: BASE_FAQ.de.after_q, a: BASE_FAQ.de.after_a },
      ],
      en: [
        { q: "Do you guarantee specific rankings?", a: "No — nobody serious does. We guarantee clean methodology, honest reports and sustainable strategies instead of short-term tricks." },
        { q: "Can you do SEO only or Ads only?", a: "Yes, both is possible. But maximum impact comes when SEO, Ads and conversion optimization work together." },
        { q: "What does reporting look like?", a: "You get a Looker Studio dashboard plus a monthly plain-language report: what worked, what we learned, what we'll try next." },
        { q: BASE_FAQ.en.timeline_q, a: BASE_FAQ.en.timeline_a },
        { q: BASE_FAQ.en.cost_q, a: BASE_FAQ.en.cost_a },
        { q: BASE_FAQ.en.after_q, a: BASE_FAQ.en.after_a },
      ],
    },
  },

  "content-branding": {
    icon: "Palette",
    seoTitle: { de: "Content & Branding | Joshi FusionTech Media", en: "Content & Branding | Joshi FusionTech Media" },
    seoDesc: {
      de: "Markenstrategie, Content-Produktion, Social Media und Storytelling für Unternehmen, die durch klare Botschaften wachsen wollen — Berlin & EU.",
      en: "Brand strategy, content production, social media and storytelling for companies that want to grow through clear messaging — Berlin & EU.",
    },
    hero: {
      de: { kicker: "Content & Branding", h1: "Eine Marke, die klar spricht — und konsistent wirkt.", sub: "Wir definieren Markenstimme, Content-Strategie und Storytelling und produzieren die Assets, die deine Reichweite, Wiedererkennung und Conversion gleichzeitig anheben." },
      en: { kicker: "Content & Branding", h1: "A brand that speaks clearly — and stays consistent.", sub: "We define brand voice, content strategy and storytelling and produce the assets that lift your reach, recognition and conversion at the same time." },
    },
    deliver: {
      de: { title: "Was wir liefern", items: [
        { t: "Content-Produktion", d: "Editorial Posts, Long-Form-Beiträge, Whitepapers, Reports." },
        { t: "Social-Media-Content", d: "LinkedIn, Instagram, YouTube — von der Idee bis zum geplanten Post." },
        { t: "Brand Messaging", d: "Positionierung, Value-Propositions, Tagline und Tonalität." },
        { t: "Copywriting", d: "Website-Texte, E-Mails, Sales-Decks, Landingpages." },
        { t: "Content-Strategie", d: "Redaktionsplan, Themencluster, Distributionsplan." },
      ]},
      en: { title: "What we deliver", items: [
        { t: "Content production", d: "Editorial posts, long-form articles, whitepapers, reports." },
        { t: "Social-media content", d: "LinkedIn, Instagram, YouTube — from idea to scheduled post." },
        { t: "Brand messaging", d: "Positioning, value propositions, tagline and tone of voice." },
        { t: "Copywriting", d: "Website copy, emails, sales decks, landing pages." },
        { t: "Content strategy", d: "Editorial plan, topic clusters, distribution plan." },
      ]},
    },
    process: {
      de: { title: "Prozess", steps: [
        { t: "Brand-Audit", d: "Wir analysieren bestehende Sprache, Visuals und Wahrnehmung." },
        { t: "Positionierung", d: "Wir definieren die Botschaft — kurz, klar, differenzierend." },
        { t: "Content-Plan", d: "Wir mappen Themen auf Personas und Kanäle." },
        { t: "Produktion", d: "Wir produzieren Texte, Grafiken und Social Posts." },
        { t: "Distribution", d: "Wir planen, posten und messen Reichweite & Engagement." },
      ]},
      en: { title: "Process", steps: [
        { t: "Brand audit", d: "We analyze existing language, visuals and perception." },
        { t: "Positioning", d: "We define the message — short, clear, differentiating." },
        { t: "Content plan", d: "We map topics to personas and channels." },
        { t: "Production", d: "We produce copy, graphics and social posts." },
        { t: "Distribution", d: "We schedule, post and measure reach & engagement." },
      ]},
    },
    deliverables: {
      de: { title: "Typische Deliverables", items: ["Brand Voice Guide", "Content-Kalender (90-Tage)", "Social-Templates (Figma + Canva)", "SEO-optimierte Long-Form-Posts", "E-Mail- und Newsletter-Sequenzen", "Pitch- & Sales-Decks"] },
      en: { title: "Typical deliverables", items: ["Brand voice guide", "Content calendar (90 days)", "Social templates (Figma + Canva)", "SEO-optimized long-form posts", "Email & newsletter sequences", "Pitch & sales decks"] },
    },
    faq: {
      de: [
        { q: "Schreibt ihr auch auf Deutsch und Englisch?", a: "Ja — native auf beidem, plus weitere EU-Sprachen über unser Partner-Netzwerk." },
        { q: "Produziert ihr auch Video- und Motion-Content?", a: "Ja, in einem Lean-Setup: Short-Form für Social, Explainer und Customer-Stories." },
        { q: "Übernehmt ihr auch das tägliche Community-Management?", a: "Optional ja — als Care-Plan oder gemeinsam mit deinem internen Team." },
        { q: BASE_FAQ.de.timeline_q, a: BASE_FAQ.de.timeline_a },
        { q: BASE_FAQ.de.cost_q, a: BASE_FAQ.de.cost_a },
      ],
      en: [
        { q: "Do you write in both German and English?", a: "Yes — native in both, plus more EU languages through our partner network." },
        { q: "Do you produce video and motion content?", a: "Yes, in a lean setup: short-form for social, explainers and customer stories." },
        { q: "Do you handle daily community management?", a: "Optional yes — as a care plan or together with your in-house team." },
        { q: BASE_FAQ.en.timeline_q, a: BASE_FAQ.en.timeline_a },
        { q: BASE_FAQ.en.cost_q, a: BASE_FAQ.en.cost_a },
      ],
    },
  },

  "brand-design": {
    icon: "Sparkles",
    seoTitle: { de: "Brand Design & visuelle Identität | Joshi FusionTech Media", en: "Brand Design & Visual Identity | Joshi FusionTech Media" },
    seoDesc: {
      de: "Logo-Design, Brand-Identität, Designsysteme und Marketing-Materialien für Unternehmen, die sichtbar und wiedererkennbar werden wollen.",
      en: "Logo design, brand identity, design systems and marketing materials for companies that want to be visible and recognizable.",
    },
    hero: {
      de: { kicker: "Brand Design", h1: "Eine visuelle Identität, die im Gedächtnis bleibt.", sub: "Vom Logo bis zum kompletten Designsystem entwickeln wir Markenwelten, die auf Pitch-Decks, Websites und Social genauso konsistent wirken wie im echten Leben." },
      en: { kicker: "Brand Design", h1: "A visual identity that sticks.", sub: "From logo to complete design system, we build brand worlds that look consistent on pitch decks, websites, social — and in real life." },
    },
    process: {
      de: { title: "Design-Prozess", steps: [
        { t: "Discovery", d: "Mood-Research, Wettbewerb, Persönlichkeit der Marke." },
        { t: "Konzepte", d: "2–3 distinkte Richtungen, jeweils mit Logo, Farben, Typo." },
        { t: "Verfeinerung", d: "Wir entwickeln die gewählte Richtung in die Tiefe weiter." },
        { t: "System", d: "Wir liefern ein Designsystem mit Komponenten, Templates und Guidelines." },
        { t: "Rollout", d: "Wir wenden das System auf Website, Decks und Marketing-Materialien an." },
      ]},
      en: { title: "Design process", steps: [
        { t: "Discovery", d: "Mood research, competition, brand personality." },
        { t: "Concepts", d: "2–3 distinct directions, each with logo, colors, type." },
        { t: "Refinement", d: "We deepen the chosen direction." },
        { t: "System", d: "We deliver a design system with components, templates and guidelines." },
        { t: "Rollout", d: "We apply the system to website, decks and marketing materials." },
      ]},
    },
    deliverables: {
      de: { title: "Was du bekommst", items: ["Logo (primär, sekundär, Favicon, Social)", "Farbpalette inkl. Accessibility-Kontraste", "Typografie-System (Display, Body, Mono)", "Designsystem (Buttons, Cards, Icons, Layouts)", "Visiten-, Briefpapier-, Pitch-Templates", "Brand Guidelines (PDF + Figma)"] },
      en: { title: "What you get", items: ["Logo (primary, secondary, favicon, social)", "Color palette including accessibility contrasts", "Typography system (display, body, mono)", "Design system (buttons, cards, icons, layouts)", "Business-card, letterhead, pitch templates", "Brand guidelines (PDF + Figma)"] },
    },
    cases: {
      de: { title: "Portfolio-Beispiele", items: [
        { industry: "B2B Tech-Startup", challenge: "Identität zwischen Investor:innen und Endkund:innen kommunizieren.", solution: "Dual-Tone Designsystem mit Investor-Deck-Theme und Customer-Site-Theme.", result: "Klarere Positionierung und konsistente Außendarstellung." },
        { industry: "Boutique-Dienstleister", challenge: "Branding wirkte amateurhaft.", solution: "Komplettes Re-Branding inkl. Designsystem und Website.", result: "Wahrgenommene Premium-Positionierung und höhere Preisbereitschaft." },
      ]},
      en: { title: "Portfolio examples", items: [
        { industry: "B2B tech startup", challenge: "Communicate identity to investors and end customers.", solution: "Dual-tone design system with investor-deck theme and customer-site theme.", result: "Clearer positioning and consistent external presentation." },
        { industry: "Boutique service provider", challenge: "Branding looked amateur.", solution: "Complete rebrand including design system and website.", result: "Premium positioning perceived and higher price acceptance." },
      ]},
    },
    faq: {
      de: [
        { q: "Bekomme ich die Quelldateien?", a: "Ja — Figma, SVG, PDF, alles editierbar. Du bist Eigentümer:in." },
        { q: "Designt ihr auch nur ein Logo?", a: "Ja, aber wir empfehlen mindestens das Logo + ein minimales Farbe/Typo-System, damit das Logo nicht inkonsistent eingesetzt wird." },
        { q: "Macht ihr auch Print?", a: "Ja — Visitenkarten, Broschüren, Messe-Material, Verpackung über unser Partner-Netzwerk." },
        { q: BASE_FAQ.de.timeline_q, a: BASE_FAQ.de.timeline_a },
        { q: BASE_FAQ.de.cost_q, a: BASE_FAQ.de.cost_a },
      ],
      en: [
        { q: "Do I get the source files?", a: "Yes — Figma, SVG, PDF, all editable. You own them." },
        { q: "Do you design only a logo?", a: "Yes, but we recommend at least logo + a minimal color/type system, so the logo isn't used inconsistently later." },
        { q: "Do you also do print?", a: "Yes — business cards, brochures, trade-show materials, packaging via our partner network." },
        { q: BASE_FAQ.en.timeline_q, a: BASE_FAQ.en.timeline_a },
        { q: BASE_FAQ.en.cost_q, a: BASE_FAQ.en.cost_a },
      ],
    },
  },

  "strategy-consulting": {
    icon: "Compass",
    seoTitle: { de: "Strategie & Tech-Beratung | Joshi FusionTech Media", en: "Strategy & Tech Consulting | Joshi FusionTech Media" },
    seoDesc: {
      de: "Digitale Transformation, Tech-Beratung und Wachstumsstrategie für Unternehmen, die ihre nächsten 12–24 Monate planen — pragmatisch, neutral, umsetzungsorientiert.",
      en: "Digital transformation, tech consulting and growth strategy for businesses planning the next 12–24 months — pragmatic, neutral, execution-oriented.",
    },
    hero: {
      de: { kicker: "Strategie & Beratung", h1: "Strategie, die ausführbar ist — nicht nur ein Slide-Deck.", sub: "Wir helfen Geschäftsführungen und Teams, digitale Transformation und Wachstum zu planen — mit klarer Roadmap, machbaren Meilensteinen und Fokus auf Umsetzung statt PowerPoint." },
      en: { kicker: "Strategy & Consulting", h1: "Strategy that's executable — not just a slide deck.", sub: "We help leadership teams plan digital transformation and growth — with a clear roadmap, achievable milestones and a focus on execution rather than PowerPoint." },
    },
    framework: {
      de: { title: "Unser Beratungs-Framework", items: [
        { t: "Digital Transformation", d: "Tooling-, Daten- und Prozess-Audit. Pragmatische Modernisierung statt Big-Bang." },
        { t: "Business-Strategie", d: "Positionierung, GTM und Pricing — basierend auf Markt- und Wettbewerbsanalyse." },
        { t: "Prozess-Verbesserung", d: "Wir mappen Workflows, eliminieren Engpässe und automatisieren wo sinnvoll." },
        { t: "Tech-Beratung", d: "Architektur-Reviews, Make-vs-Buy-Entscheidungen, Tech-Hiring-Pläne." },
        { t: "Wachstumsberatung", d: "Funnel-Analyse, Channel-Strategie und Skalierungsplan." },
      ]},
      en: { title: "Our consulting framework", items: [
        { t: "Digital transformation", d: "Tooling, data and process audit. Pragmatic modernization instead of big-bang." },
        { t: "Business strategy", d: "Positioning, GTM and pricing — based on market and competitor analysis." },
        { t: "Process improvement", d: "We map workflows, remove bottlenecks and automate where it pays." },
        { t: "Tech consulting", d: "Architecture reviews, make-vs-buy decisions, tech-hiring plans." },
        { t: "Growth consulting", d: "Funnel analysis, channel strategy and scaling plan." },
      ]},
    },
    process: {
      de: { title: "Roadmap-Prozess", steps: [
        { t: "Kick-off", d: "Wir verstehen Ziele, Constraints und Status quo." },
        { t: "Audit", d: "Daten, Tools, Team und Markt analysieren." },
        { t: "Workshop", d: "Gemeinsame Erarbeitung der Strategie mit deinem Team." },
        { t: "Roadmap", d: "12–24-Monats-Plan mit Meilensteinen, Owners und KPIs." },
        { t: "Begleitung", d: "Optional als On-Demand-Sparring oder monatlicher Retainer." },
      ]},
      en: { title: "Roadmap process", steps: [
        { t: "Kick-off", d: "We understand goals, constraints and status quo." },
        { t: "Audit", d: "Analyze data, tools, team and market." },
        { t: "Workshop", d: "Co-create the strategy with your team." },
        { t: "Roadmap", d: "12–24-month plan with milestones, owners and KPIs." },
        { t: "Companion", d: "Optional as on-demand sparring or monthly retainer." },
      ]},
    },
    deliverables: {
      de: { title: "Typische Deliverables", items: ["Strategic Roadmap (12–24 Monate)", "Audit-Report (Tools / Daten / Prozesse)", "Markt- & Wettbewerbsanalyse", "Tech-Architektur-Review", "Hiring- und Vendor-Plan", "Monatliches Strategie-Sparring (optional)"] },
      en: { title: "Typical deliverables", items: ["Strategic roadmap (12–24 months)", "Audit report (tools / data / processes)", "Market & competitor analysis", "Tech architecture review", "Hiring & vendor plan", "Monthly strategy sparring (optional)"] },
    },
    faq: {
      de: [
        { q: "Müsst ihr unbedingt umsetzen, was ihr beratet?", a: "Nein. Wir können die Strategie übergeben und du setzt sie intern oder mit deinen bestehenden Partnern um. Wenn du willst, übernehmen wir die Umsetzung." },
        { q: "Sind eure Empfehlungen vendor-neutral?", a: "Ja. Wir verkaufen keine Lizenzen und haben keine Kickback-Modelle mit Tools. Wir empfehlen, was im Kontext am besten passt." },
        { q: "Wie viele Workshops sind typisch?", a: "Üblicherweise 2–4 Workshop-Tage über 3–6 Wochen plus async Vorbereitung und Nachbereitung." },
        { q: BASE_FAQ.de.timeline_q, a: BASE_FAQ.de.timeline_a },
        { q: BASE_FAQ.de.cost_q, a: BASE_FAQ.de.cost_a },
      ],
      en: [
        { q: "Do you have to execute what you advise?", a: "No. We can hand the strategy over and you implement internally or with your existing partners. If you want, we'll execute." },
        { q: "Are your recommendations vendor-neutral?", a: "Yes. We don't sell licenses and have no kick-back deals with tools. We recommend what fits in your context." },
        { q: "How many workshops are typical?", a: "Usually 2–4 workshop days over 3–6 weeks plus async preparation and follow-up." },
        { q: BASE_FAQ.en.timeline_q, a: BASE_FAQ.en.timeline_a },
        { q: BASE_FAQ.en.cost_q, a: BASE_FAQ.en.cost_a },
      ],
    },
  },
};

export const SERVICE_ORDER = [
  "ai-automation",
  "web-development",
  "digital-marketing",
  "content-branding",
  "brand-design",
  "strategy-consulting",
];
